import { Link } from "react-router-dom";
import { ShieldCheck, HeartHandshake, Wrench, Clock, Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { Button } from "@/components/ui/button";
import { business, heroImages } from "@/data/site";

const values = [
  { icon: ShieldCheck, title: "Integrity", body: "Honest assessments and fair work. We tell you what you need — not what sells." },
  { icon: Clock, title: "Show Up On Time", body: "When we say we'll be there, we're there. Your independence can't wait." },
  { icon: Wrench, title: "Real Expertise", body: "Hands-on knowledge of powered and non-powered devices from every major brand." },
  { icon: HeartHandshake, title: "Respect", body: "Veterans, caregivers and families are treated with the dignity they deserve." },
];

export default function About() {
  return (
    <div>
      <PageHeader
        eyebrow="About JRs"
        title="Veteran owned. Service driven."
        description="JRs Veteran Owned LLC was built on a simple idea: treat people the way you'd want your own family treated, and stand behind your work."
      />

      <section className="section-py">
        <div className="mx-auto grid max-w-7xl items-center gap-12 container-px lg:grid-cols-2">
          <Reveal>
            <div className="relative">
              <img
                src={heroImages.caregiver}
                alt="Caregiver assisting a client with mobility equipment"
                className="w-full rounded-3xl object-cover shadow-card"
              />
              <div className="absolute -bottom-6 -right-4 hidden rounded-2xl border border-border bg-card p-5 shadow-card sm:block">
                <p className="font-display text-3xl font-bold text-primary">Veteran</p>
                <p className="text-sm text-muted-foreground">Owned &amp; operated</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3.5 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              Our story
            </span>
            <h2 className="mt-4 text-3xl font-bold text-foreground sm:text-4xl">
              Meet {business.owner}
            </h2>
            <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-muted-foreground">
              <p>
                After serving our country, {business.owner} brought the same discipline and
                commitment to a new mission: helping neighbors stay mobile, comfortable and
                independent in their own homes.
              </p>
              <p>
                Based in {business.location} ({business.region}) and {business.credentials},
                {" "}{business.legalName} delivers, installs and repairs medical mobility equipment
                across Central Pennsylvania and the Tri-State region. From lift chairs and
                stair lifts to power chairs, scooters and ADA home improvements, we handle
                powered and non-powered devices alike.
              </p>
              <p>
                We're especially proud to serve fellow Veterans and to work with the VA. When
                you call, you reach a real person who genuinely wants to help.
              </p>
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild variant="brand" size="lg">
                <a href={business.phoneHref}><Phone className="h-4 w-4" /> Call {business.phone}</a>
              </Button>
              <Button asChild variant="outlineDark" size="lg">
                <Link to="/contact">Request service <ArrowRight className="h-4 w-4" /></Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-muted section-py">
        <div className="mx-auto max-w-7xl container-px">
          <SectionHeading eyebrow="What we stand for" title="Values we carry into every home" />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={(i % 4) * 0.06}>
                  <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-7 shadow-soft">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 text-lg font-bold text-foreground">{v.title}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{v.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-brand-deep">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 py-16 text-center container-px">
          <ShieldCheck className="h-10 w-10 text-accent" />
          <h2 className="max-w-2xl text-3xl font-bold text-white sm:text-4xl">
            Proudly supporting Veteran mental health &amp; suicide awareness
          </h2>
          <p className="max-w-xl text-white/70">
            The green ribbon in our logo represents our commitment to the well-being of those
            who served. You're never alone — reach out anytime.
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/70">
            {["Veterans served", "VA-friendly", "Community focused"].map((t) => (
              <span key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> {t}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
